import { computed, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fillDirectory, getDirectory } from '@/directory'
import { defineLocalStore } from '@/utils/localStore'
import type { DocumentTypeDefinition } from '@/utils/types/documentType'
import type {
  DocumentTypeField,
  DocumentTypeFieldDefinition,
} from '@/utils/types/documentTypeField'
import { registrationJournalsByDefinition } from '@/requests/registrationJournal'
import type { RegistrationJournalDefinition } from '@/utils/types/registrationJournal'
import type {
  AttributeSearchConfig,
  AttributeSearchFieldValue,
  AttributeSearchForm,
  FullTextSearchForm,
  RawSearchResult,
  SearchMode,
  SearchResult,
} from '@/utils/types/search'
import { edsSearchAttribute, edsSearchFullText } from '@/requests/edsSearch'
import type { Query } from 'best-modules/utils'

const ATTRIBUTE_CONFIG_STORAGE_KEY = 'eds-search-attribute-configs'

const getDefaultAttributeForm = (): AttributeSearchForm => ({
  docTypeClassId: null,
  docTypeDefinitionId: null,
  registrationJournalId: null,
  title: '',
  departmentId: null,
  registrationDateFrom: null,
  registrationDateTo: null,
  fields: [],
})

const getDefaultFullTextForm = (): FullTextSearchForm => ({
  query: '',
  scopes: {
    title: true,
    body: true,
    attributes: false,
  },
  options: {
    exactPhrase: false,
    caseSensitive: false,
  },
})

const getDefaultFieldValue = (fieldType: string): AttributeSearchFieldValue => {
  if (fieldType === 'boolean') return false
  if (fieldType === 'multiselect') return []

  return null
}

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const getAttributeFormSnapshot = (form: AttributeSearchForm) => {
  return JSON.stringify(form)
}

const getSavedAttributeConfigs = (): AttributeSearchConfig[] => {
  try {
    return JSON.parse(
      localStorage.getItem(ATTRIBUTE_CONFIG_STORAGE_KEY) || '[]'
    )
  } catch {
    return []
  }
}

const setSavedAttributeConfigs = (configs: AttributeSearchConfig[]) => {
  localStorage.setItem(ATTRIBUTE_CONFIG_STORAGE_KEY, JSON.stringify(configs))
}

const normalizeAttributeForm = (
  form: Partial<AttributeSearchForm>
): AttributeSearchForm => {
  const attributeForm = { ...form } as Partial<AttributeSearchForm> & {
    registrationNumber?: string
  }
  delete attributeForm.registrationNumber

  return {
    ...getDefaultAttributeForm(),
    ...attributeForm,
    fields: form.fields || [],
  }
}

const normalizeSearchResult = (item: RawSearchResult): SearchResult => ({
  id: item.id,
  uuid: item.uuid,
  documentNumber: item.documentNumber || '',
  title: item.title || '',
  registrationJournal: item.registrationJournal || '',
  registrationDate: item.registrationDate || item.date || '',
  department: item.department || '',
  author: item.author || '',
  createdAt: item.createdAt || item.created_at || '',
  status: item.status || 'onRegistration',
})

export const { initLocalStore: initSearch, useLocalStore: useSearch } =
  defineLocalStore('search', () => {
    const route = useRoute()
    const router = useRouter()
    const attributeForm = reactive(getDefaultAttributeForm())
    const fullTextForm = reactive(getDefaultFullTextForm())
    const resultMode = ref<null | SearchMode>(null)
    const resultTitle = ref('')
    const resultsCount = ref(0)
    const searchRequestKey = ref(0)
    const attributeConfigName = ref('')
    const lastAttributeSearchSnapshot = ref('')
    const availableRegistrationJournals = ref<RegistrationJournalDefinition[]>(
      []
    )
    const savedAttributeConfigs = reactive<AttributeSearchConfig[]>(
      getSavedAttributeConfigs()
    )
    const docTypeDefinitions = computed(
      () => getDirectory('docTypeDefinitions') as DocumentTypeDefinition[]
    )
    const fieldDefinitions = computed(
      () => getDirectory('fieldDefinitions') as DocumentTypeFieldDefinition[]
    )
    const selectedDocTypeDefinition = computed(() => {
      return docTypeDefinitions.value.find(
        item => item.id === attributeForm.docTypeDefinitionId
      )
    })
    const selectedFieldIds = computed(() => {
      return new Set(attributeForm.fields.map(item => item.fieldDefinitionId))
    })
    const availableAttributeFields = computed<
      (DocumentTypeField | DocumentTypeFieldDefinition)[]
    >(() => {
      if (selectedDocTypeDefinition.value?.fields?.length) {
        const availableDocTypeFields =
          selectedDocTypeDefinition.value.fields.filter(item => {
            return (
              !item.isHidden &&
              !selectedFieldIds.value.has(item.fieldDefinitionId)
            )
          })

        return availableDocTypeFields
      }

      const availableFieldDefinitions = fieldDefinitions.value.filter(item => {
        return item.isActive !== false && !selectedFieldIds.value.has(item.id)
      })

      return availableFieldDefinitions
    })
    const hasAttributeSearchParams = computed(() => {
      return Boolean(
        attributeForm.docTypeDefinitionId ||
          attributeForm.registrationJournalId ||
          attributeForm.title.trim() ||
          attributeForm.departmentId ||
          attributeForm.registrationDateFrom ||
          attributeForm.registrationDateTo ||
          attributeForm.fields.length
      )
    })
    const canSaveAttributeConfig = computed(() => {
      return (
        hasAttributeSearchParams.value &&
        lastAttributeSearchSnapshot.value ===
          getAttributeFormSnapshot(attributeForm)
      )
    })

    const fillSearchDictionaries = () => {
      fillDirectory([
        'docTypeClasses',
        'docTypeDefinitions',
        'fieldDefinitions',
        'internalDepartments',
      ])
    }

    const resetResults = () => {
      resultMode.value = null
      resultTitle.value = ''
      resultsCount.value = 0
    }

    const fillRegistrationJournalsByDefinition = async (
      docTypeDefinitionId = attributeForm.docTypeDefinitionId
    ) => {
      availableRegistrationJournals.value = []

      if (!docTypeDefinitionId) return

      const journals = await registrationJournalsByDefinition(
        docTypeDefinitionId
      )

      if (attributeForm.docTypeDefinitionId === docTypeDefinitionId) {
        availableRegistrationJournals.value = journals
      }
    }

    const search = async (mode: SearchMode, title: string) => {
      await router.replace({
        name: route.name,
        query: {
          ...route.query,
          page: 1,
        },
      })

      resultMode.value = mode
      resultTitle.value = title
      searchRequestKey.value += 1

      if (mode === 'attribute') {
        lastAttributeSearchSnapshot.value = hasAttributeSearchParams.value
          ? getAttributeFormSnapshot(attributeForm)
          : ''
      }
    }

    const getSearchResults = async (query: Query) => {
      if (!resultMode.value) {
        return {
          data: [],
          current_page: 1,
          from: 0,
          last_page: 1,
          per_page: 20,
          to: 0,
          total: 0,
        }
      }

      const results =
        resultMode.value === 'attribute'
          ? await edsSearchAttribute(attributeForm, query)
          : await edsSearchFullText(fullTextForm, query)

      resultsCount.value = results.total

      return {
        ...results,
        data: results.data.map(normalizeSearchResult),
      }
    }

    const addAttributeField = (
      field: DocumentTypeField | DocumentTypeFieldDefinition
    ) => {
      const isDefinition = 'fieldType' in field
      const fieldDefinitionId = isDefinition
        ? field.id
        : field.fieldDefinitionId
      const fieldType = isDefinition
        ? field.fieldType
        : field.fieldDefinition.fieldType
      const entitySource = field.entitySource ?? null

      const attributeField = {
        fieldDefinitionId,
        label: isDefinition
          ? field.name
          : (field as DocumentTypeField).label ||
            (field as DocumentTypeField).fieldDefinition.name,
        fieldType,
        optionsJson: field.optionsJson ?? null,
        entitySource,
        value: getDefaultFieldValue(fieldType),
      }

      attributeForm.fields.push(attributeField)

      if (entitySource && attributeForm.docTypeDefinitionId) {
        fillDirectory([entitySource as any])
      }
    }

    const setAttributeDocTypeDefinition = (
      docTypeDefinitionId: null | number
    ) => {
      attributeForm.docTypeDefinitionId = docTypeDefinitionId
      attributeForm.registrationJournalId = null
      attributeForm.fields.splice(0)
      void fillRegistrationJournalsByDefinition(docTypeDefinitionId)
    }

    const removeAttributeField = (fieldDefinitionId: number) => {
      const fieldIndex = attributeForm.fields.findIndex(
        item => item.fieldDefinitionId === fieldDefinitionId
      )

      if (fieldIndex !== -1) attributeForm.fields.splice(fieldIndex, 1)
    }

    const resetAttributeForm = () => {
      Object.assign(attributeForm, getDefaultAttributeForm())
      availableRegistrationJournals.value = []
      attributeConfigName.value = ''
      lastAttributeSearchSnapshot.value = ''
      resetResults()
    }

    const getAttributeConfigsByClass = (classId: number) => {
      return savedAttributeConfigs.filter(config => config.classId === classId)
    }

    const saveAttributeConfig = (classId: number) => {
      if (!canSaveAttributeConfig.value || !attributeConfigName.value.trim()) {
        return
      }

      const config: AttributeSearchConfig = {
        id: Date.now(),
        classId,
        name: attributeConfigName.value.trim(),
        form: clone(attributeForm),
      }

      savedAttributeConfigs.push(config)
      setSavedAttributeConfigs(savedAttributeConfigs)
      attributeConfigName.value = ''
    }

    const applyAttributeConfig = (config: AttributeSearchConfig) => {
      Object.assign(attributeForm, normalizeAttributeForm(clone(config.form)))
      attributeForm.fields
        .filter(field => field.entitySource)
        .forEach(field => fillDirectory([field.entitySource as any]))
      void fillRegistrationJournalsByDefinition()
      lastAttributeSearchSnapshot.value =
        getAttributeFormSnapshot(attributeForm)
      resultMode.value = null
      resultTitle.value = ''
      resultsCount.value = 0
    }

    const removeAttributeConfig = (configId: number) => {
      const configIndex = savedAttributeConfigs.findIndex(
        config => config.id === configId
      )

      if (configIndex === -1) return

      savedAttributeConfigs.splice(configIndex, 1)
      setSavedAttributeConfigs(savedAttributeConfigs)
    }

    const resetFullTextForm = () => {
      Object.assign(fullTextForm, getDefaultFullTextForm())
      resetResults()
    }

    return {
      attributeForm,
      fullTextForm,
      resultMode,
      resultTitle,
      resultsCount,
      searchRequestKey,
      attributeConfigName,
      savedAttributeConfigs,
      hasAttributeSearchParams,
      canSaveAttributeConfig,
      docTypeDefinitions,
      fieldDefinitions,
      selectedDocTypeDefinition,
      availableRegistrationJournals,
      availableAttributeFields,
      fillSearchDictionaries,
      addAttributeField,
      setAttributeDocTypeDefinition,
      removeAttributeField,
      search,
      getSearchResults,
      resetResults,
      resetAttributeForm,
      resetFullTextForm,
      getAttributeConfigsByClass,
      saveAttributeConfig,
      applyAttributeConfig,
      removeAttributeConfig,
    }
  })
